/**
 * Modular Dashboard Component
 * Main dashboard component using the new modular architecture
 */

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ErrorBoundary from '../common/ErrorBoundary';
import LoadingSpinner from '../common/LoadingSpinner';

// Import sub-modules
import { StatsGrid, useStats } from './dashboard.stats';
import { QuickActions, RecentActivity } from './dashboard.widgets';
import { ActiveReservationsList, ActivitySummary, useActiveReservations } from './dashboard.activity';
import { useDashboardEvents } from './dashboard.integration';

const ModularDashboard = ({ layout = 'default', customization = {} }) => {
  const { loading: authLoading } = useAuth();
  const [lastUpdated, setLastUpdated] = useState(new Date());
  
  // Dashboard data hooks
  const {
    stats,
    loading: statsLoading,
    error: statsError,
    fromCache: statsFromCache,
    refresh: refreshStats
  } = useStats({ autoRefresh: true });

  const {
    reservations,
    loading: reservationsLoading,
    error: reservationsError,
    refresh: refreshReservations,
    activeCount,
    overdueCount
  } = useActiveReservations({ 
    autoRefresh: true, 
    maxReservations: 10 
  });

  // Real-time event handling
  // TODO: Implement SSE endpoint on server at /api/events/dashboard
  const { connected, lastEvent, eventCount } = useDashboardEvents({
    autoConnect: false, // Disabled until SSE endpoint is implemented
    onEvent: (eventType, payload) => {
      setLastUpdated(new Date());
      
      // Refresh data based on event type
      switch (eventType) {
        case 'reservation-created':
        case 'reservation-updated':
        case 'reservation-cancelled':
          refreshReservations();
          refreshStats();
          break;
        case 'inventory-status-changed':
          refreshStats();
          break;
        case 'payment-processed':
          refreshStats();
          break;
        case 'stats-updated':
          refreshStats();
          break;
        default:
          break;
      }
    }
  });

  const loading = authLoading || statsLoading;

  if (loading && !stats && !reservations?.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const renderHeader = () => (
    <div className="bg-white rounded-lg shadow p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Welcome to your management system
          </p>
        </div>
        <div className="text-left sm:text-right">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            {connected && (
              <div className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-600 rounded-full mr-2 animate-pulse"></div>
                <span className="text-xs sm:text-sm">Live Updates</span>
              </div>
            )}
            <div className="text-xs sm:text-sm text-gray-500">
              <p>Last updated: {lastUpdated.toLocaleTimeString()}</p>
              {statsFromCache && (
                <p className="text-xs text-blue-600">Data from cache</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDefaultLayout = () => (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      {renderHeader()}

      {/* Statistics Grid */}
      <ErrorBoundary fallback={<div className="p-4 text-red-600">Error loading statistics</div>}>
        <StatsGrid 
          stats={stats} 
          loading={statsLoading} 
          error={statsError}
          onRefresh={refreshStats}
        />
      </ErrorBoundary>

      {/* Activity Summary */}
      <ErrorBoundary fallback={<div className="p-4 text-red-600">Error loading activity summary</div>}>
        <ActivitySummary
          stats={stats}
          reservations={reservations}
          loading={statsLoading || reservationsLoading}
        />
      </ErrorBoundary>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        {/* Quick Actions */}
        <ErrorBoundary fallback={<div className="p-4 text-red-600">Error loading quick actions</div>}>
          <QuickActions />
        </ErrorBoundary>

        {/* Recent Activity */}
        <ErrorBoundary fallback={<div className="p-4 text-red-600">Error loading recent activity</div>}>
          <RecentActivity
            stats={stats}
            loading={statsLoading}
            maxItems={5}
          />
        </ErrorBoundary>
      </div>

      {/* Active Reservations */}
      <ErrorBoundary fallback={<div className="p-4 text-red-600">Error loading active reservations</div>}>
        <ActiveReservationsList
          reservations={reservations}
          loading={reservationsLoading}
          error={reservationsError}
          maxItems={5}
          showOverdueWarning={true}
        />
      </ErrorBoundary>

      {/* Debug Info (Development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-gray-100 rounded-lg p-4 text-xs text-gray-600">
          <p>🔧 Debug Info:</p>
          <p>Events: {eventCount} | Connected: {connected ? '✅' : '❌'} | Active: {activeCount} | Overdue: {overdueCount}</p>
          {lastEvent && (
            <p>Last Event: {lastEvent.type} at {lastEvent.timestamp.toLocaleTimeString()}</p>
          )}
        </div>
      )}
    </div>
  );

  const renderCompactLayout = () => (
    <div className="space-y-4">
      {renderHeader()}
      
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <StatsGrid stats={stats} loading={statsLoading} error={statsError} onRefresh={refreshStats} />
        </div>
        <div>
          <QuickActions layout="grid-cols-1" />
        </div>
      </div>

      <ActiveReservationsList
        reservations={reservations}
        loading={reservationsLoading}
        error={reservationsError}
        maxItems={3}
      />
    </div>
  );

  // Render based on layout
  switch (layout) {
    case 'compact':
      return renderCompactLayout();
    case 'default':
    default:
      return renderDefaultLayout();
  }
};

export default ModularDashboard;